import React, {DragEvent, useState} from 'react';
import {CalculatedOption, Item, KoluSet} from "../../config/interfaces";
import {PlacementOutside} from "../../config/enums";
import {Alert, Button, ButtonGroup} from "react-bootstrap";
import {placementOutsideOptions, roomTypes} from "../../config/options";
import ButtonGroupSelect from "../ButtonGroupSelect";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faInfoCircle,
    faPlus,
    faSlidersH,
    faTimes,
    faUndo
} from "@fortawesome/free-solid-svg-icons";
import {NotificationManager} from "react-notifications";
import {cloneDeep} from 'lodash';

interface SetsOverviewProps {
    calculatedOptions: CalculatedOption[];
    setCalculatedOptions: (options: CalculatedOption[]) => void;
    sets: KoluSet[];
    setSets: (sets: KoluSet[]) => void;
    items: Item[];
}

/**
 * Component in stepper where the user can re-arrange sets per brand.
 * @param {SetsOverviewProps} props - The props that the SetsOverview component accepts.
 * @param {CalculatedOption[]} props.calculatedOptions - Array of calculated options, every brand that is selected has one option in this array.
 * @param {Function} props.setCalculatedOptions - Function to change calculated options.
 * @param {Brand} props.chosenBrand - The chosen brand to display the sets of.
 * @param {Function} props.setChosenBrand - Function to change the chosen brand.
 * @param {KoluSet[]} props.sets - The sets that currently should be displayed.
 * @param {Function} props.setSets - Function to update sets.
 * @param {Item[]} props.items - List of possible airco items that can be chosen as perfect combination after set changes.
 * @constructor
 */
const SetsOverview = (props: SetsOverviewProps) => {
    const {calculatedOptions, items, setCalculatedOptions} = props; // sets, setSets,

    const [advancedMode, setAdvancedMode] = useState(false);

    const addSet = () => {
        const newCalculatedOptions = [];
        for (let calculatedOption of calculatedOptions) {
            const calculatedOptionCopy = cloneDeep(calculatedOption);
            calculatedOptionCopy.sets.push({
                item: null,
                rooms: [],
                placementOutside: PlacementOutside.ground
            } as KoluSet);
            newCalculatedOptions.push(calculatedOptionCopy);
        }
        setCalculatedOptions(newCalculatedOptions);
    }

    const removeSet = (i: number) => {
        const newCalculatedOptions = [];
        for (let calculatedOption of calculatedOptions) {
            const calculatedOptionCopy = cloneDeep(calculatedOption);
            calculatedOptionCopy.sets.splice(i, 1);
            newCalculatedOptions.push(calculatedOptionCopy);
        }
        setCalculatedOptions(newCalculatedOptions);
    }

    const changeSet = (setStart: number, setEnd: number, i: number) => {
        const newCalculatedOptions = [];
        for (let calculatedOption of calculatedOptions) {
            const calculatedOptionCopy = cloneDeep(calculatedOption);
            const room = calculatedOptionCopy.sets[setStart].rooms.splice(i, 1)[0];
            const newRooms = [...calculatedOptionCopy.sets[setEnd].rooms, room];
            const newItem = items.filter(i => i.merk === calculatedOption.brand && i.aantalKamers === newRooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0) && i.opp >= newRooms.reduce((acc, curr) => acc + curr.surface!, 0)).sort((a, b) => a.prijs - b.prijs)[0];

            if (!newItem) {
                NotificationManager.error('Het is niet mogelijk om deze kamer aan deze set toe te voegen.');
                return;
            } else {
                calculatedOptionCopy.sets[setEnd].rooms.push(room);
                calculatedOptionCopy.sets[setEnd].item = newItem;
                calculatedOptionCopy.sets[setStart].item = items.filter(i => i.merk === calculatedOption.brand && i.aantalKamers === calculatedOptionCopy.sets[setStart].rooms.reduce((acc, curr) => acc + curr.numberOfUnits!, 0) && i.opp >= calculatedOptionCopy.sets[setStart].rooms.reduce((acc, curr) => acc + curr.surface!, 0)).sort((a, b) => a.prijs - b.prijs)[0] || null;
            }
            newCalculatedOptions.push(calculatedOptionCopy);
        }
        setCalculatedOptions(newCalculatedOptions);
    }

    const onDragStart = (e: DragEvent<HTMLFieldSetElement>, i: number, j: number) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({i, j}));
    }

    const onDragOver = (e: DragEvent<HTMLFieldSetElement>) => {
        e.preventDefault();
    }

    const onDrop = (e: DragEvent<HTMLFieldSetElement>, i: number) => {
        const values = JSON.parse(e.dataTransfer.getData('text/plain'));
        changeSet(values.i, i, values.j);
        e.dataTransfer.clearData();
    }

    const onSetResetClick = () => {
        const calculatedOptionsCopy = cloneDeep(calculatedOptions);
        calculatedOptionsCopy.map(co => {
            co.sets = cloneDeep(co.calculatedSets);
            return co;
        });
        setCalculatedOptions(calculatedOptionsCopy);
    }

    const buttons = <ButtonGroup>
        <Button variant={'main'} onClick={addSet}><FontAwesomeIcon icon={faPlus} /> Set <span className={'extra-text-only-big'}>toevoegen</span></Button>
        <Button variant={'danger'} onClick={onSetResetClick}><FontAwesomeIcon icon={faUndo} /> Reset <span className={'extra-text-only-big'}>wijzigingen</span></Button>
    </ButtonGroup>;

    return <>
        <div className={'setsTitleDiv'}>
            <h1>Combinaties</h1>
            {advancedMode && buttons}
        </div>
        {calculatedOptions[0].sets.map((set: KoluSet, i: number) => {
            return <fieldset key={'set:' + i} className={'set'} onDragOver={onDragOver} onDrop={(e) => onDrop(e, i)}>
                <legend> Combinatie #{i + 1} </legend>
                <div className={`remove-button ${set.rooms.length !== 0 ? 'd-none' : ''}`}><Button variant={'danger'} size={'sm'} onClick={() => removeSet(i)}><FontAwesomeIcon icon={faTimes} /></Button></div>
                <div className={'d-flex set-config-div'}>
                    <img src={`/images/airco-outside-unit-icon.png`} className={'mr-3'} height={100} alt={''} />
                    <div className={'flex-grow-1'}>
                        {set.rooms.map((room, j) => {
                            return <fieldset key={'set-room-' + j} className={'border d-inline-block p-2 m-2 room'} draggable onDragStart={(e) => onDragStart(e, i, j)}>
                                <legend> {roomTypes.filter(rt => rt.key === room.type)[0].name} </legend>
                                <div className={'d-flex'}>
                                    {(new Array(room.numberOfUnits).fill(0)).map((r, k) => {
                                        return <img key={'set-room-unit-' + k} src={`/images/airco-inside-unit-icon.png`} alt={'Binnenunit'} className={'flex-grow-1'} />;
                                    })}
                                </div>
                                {advancedMode && <div className={'room-buttons'}>
                                    <div className={'btn btn-circle btn-info btn-sm' + (i === 0 ? ' disabled' : '')} onClick={() => i !== 0 && changeSet(i, i - 1, j)}>
                                        <FontAwesomeIcon icon={faChevronUp} />
                                    </div>
                                    <div className={'btn btn-circle btn-info btn-sm' + (i === calculatedOptions[0].sets.length - 1 ? ' disabled' : '')} onClick={() => i !== calculatedOptions[0].sets.length - 1 && changeSet(i, i + 1, j)}>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </div>
                                </div>}
                            </fieldset>;
                        })}
                    </div>
                </div>
                <div className={'mt-5'}>
                    <strong className={'h4'}>Plaatsing buitenunit</strong>
                </div>
                <p className={'text-muted'} style={{fontSize: 12}}>Indien nodig, helpt één van onze verkopers je hier nadien graag mee verder!</p>
                <ButtonGroupSelect verticalWhenSmaller options={placementOutsideOptions} value={set.placementOutside} onChange={(value: PlacementOutside) => {
                    const newCalculatedOptions = [];
                    for (let calculatedOption of calculatedOptions) {
                        const calculatedOptionCopy = cloneDeep(calculatedOption);
                        calculatedOptionCopy.sets[i].placementOutside = value;
                        newCalculatedOptions.push(calculatedOptionCopy);
                    }
                    setCalculatedOptions(newCalculatedOptions);
                }} />
            </fieldset>;
        })}
        {advancedMode && <div className={'setsTitleDiv mt-3'}>
            <h1> </h1>
            {buttons}
        </div>}
        <Alert className={'mt-3'} variant={'info'}>
            <div className={'d-flex align-items-center'}>
                <FontAwesomeIcon icon={faInfoCircle} size={'3x'} className={'mr-5 ml-3'} />
                <div>
                    <b>Controleer de volgende zaken:</b><br />
                    <ul>
                        <li>Zijn alle units bereikbaar binnen 20 meter van een locatie buiten?</li>
                    </ul>
                    Indien je aanpassingen nodig hebt, kan je deze doorvoeren via <b>"Handmatig aanpassen"</b>. <b>Indien dit niet nodig is, kan je gewoon verder gaan.</b>
                </div>
            </div>
            <Button className={'mt-3'} variant={'info'} onClick={() => {setAdvancedMode(!advancedMode);onSetResetClick();}}><FontAwesomeIcon icon={advancedMode ? faUndo : faSlidersH} /> {advancedMode ? 'Resetten naar automatische berekening' : 'Handmatig aanpassen'}</Button>
        </Alert>
    </>
}

export default SetsOverview;